// Recipient order history — a read-only feed of orders sent to them, from
// their own perspective, with the delivery receipt downloadable once
// confirmed.
//
// On DataTable, per Web Portal v2 (TABLES.r_history). The card stack is kept
// as the phone layout: this is the role most likely to be on a small screen.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MapPin, RotateCcw } from 'lucide-react';
import { PortalShell } from '../../components/shared/PortalShell';
import { StatusPill } from '../../components/shared/primitives';
import { DataTable, type Column } from '../../components/shared/DataTable';
import { RECIPIENT_NAV } from '../_navs';
import { useRecipientNav } from '../../lib/pendingAction';
import { requests, type RequestStatus, type ServiceRequestWithItems } from '../../lib/api';
import { ReceiptLink } from '../customer/HistoryPage';

const TERMINAL: RequestStatus[] = ['confirmed', 'cancelled', 'refunded', 'disputed'];

function fmtDate(s?: string) {
  return s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
}

export function RecipientHistoryPage() {
  const q = useQuery({ queryKey: ['recipient', 'requests'], queryFn: () => requests.list() });

  const rows = useMemo(() =>
    (q.data ?? [])
      .filter((r) => TERMINAL.includes(r.status))
      .sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? '')),
    [q.data]);

  // Home carries a badge when this role owes an action, so it stays
  // visible from any screen.
  const nav = useRecipientNav(RECIPIENT_NAV);

  // The design's "From" column wants the sending family member's name and
  // "Via" the merchant. ServiceRequest carries neither on the list payload —
  // only customer_id, with no customer relation — and "Customer #7" would be
  // worse than showing the city, which is real and means something here.
  const columns: Column<ServiceRequestWithItems>[] = [
    {
      key: 'date', header: 'Date', type: 'muted', width: '130px',
      value: (r) => fmtDate(r.updated_at),
    },
    {
      key: 'ref', header: 'Reference', type: 'mono', width: '110px',
      value: (r) => `REQ-${String(r.id).padStart(4, '0')}`,
    },
    {
      key: 'what', header: 'What', type: 'strong', width: 'minmax(0,1.5fr)',
      value: (r) => (
        <>
          {r.title || r.category?.label || `Cat ${r.category_id}`}
          {r.cart_item_count > 0 && (
            <span className="font-normal text-[var(--color-muted)]"> · {r.cart_item_count} items</span>
          )}
        </>
      ),
    },
    {
      key: 'city', header: 'Delivered to', type: 'text', width: 'minmax(0,1fr)',
      value: (r) => r.city?.name ?? '—',
    },
    {
      key: 'status', header: 'Status', type: 'node', width: '120px',
      value: (r) => <StatusPill status={r.status} />,
    },
    {
      key: 'receipt', header: '', type: 'node', width: '160px',
      value: (r) => (r.status === 'confirmed' ? <ReceiptLink requestId={r.id} /> : null),
    },
  ];

  return (
    <PortalShell nav={nav} activeId="history" title="History" subtitle="Everything your family has sent you.">
      <DataTable
        title="Past deliveries"
        subtitle="Most recent first."
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={q.isLoading}
        empty="Nothing here yet. Once a delivery is confirmed, it shows up with its receipt."
        renderCard={(r) => (
          <>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[12px] text-[var(--color-body)]">
                REQ-{String(r.id).padStart(4, '0')}
              </span>
              <StatusPill status={r.status} />
            </div>
            <p className="mt-1 truncate text-[14px] font-semibold text-[var(--color-ink)]">
              {r.title || r.category?.label || `Cat ${r.category_id}`}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-[var(--color-muted)]">
              {r.city?.name && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" aria-hidden="true" /> {r.city.name}
                </span>
              )}
              <span>· {fmtDate(r.updated_at)}</span>
              {r.cart_item_count > 0 && <span>· {r.cart_item_count} items</span>}
            </div>
            {r.status === 'confirmed' && (
              <div className="mt-2 flex items-center gap-3">
                <Link
                  to={`/recipient/request/new?reorder=${r.id}`}
                  className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-brand-600 hover:underline"
                >
                  <RotateCcw className="h-3 w-3" aria-hidden="true" /> Order again
                </Link>
                <ReceiptLink requestId={r.id} />
              </div>
            )}
          </>
        )}
      />
    </PortalShell>
  );
}
